/**
 * Uses Swiper package to create a carousel for product media
 * More info: https://swiperjs.com//
 */
if (!customElements.get('product-media')) {
  class ProductMedia extends HTMLElement {
    constructor() {
      super();

      this.lastActiveBreakpoint = '';
      this.swiperInitialized = false;
      this.productModalHTML = {};
      this.selectors = {
        header: 'sticky-header',
        slider: '[data-slider]',
        thumbs: '[data-thumbs]',
        mediaItem: '[data-media-item]',
        mediaColor: 'data-media-color',
        thumbnail: '[data-thumbnail]'
      };
      this.filterMediaBy = this.dataset.galleryFilter;
      this.selectedMediaIndex =
        Number(
          this.querySelector(this.selectors.slider)?.querySelector(
            '[data-selected]'
          )?.dataset.index
        ) || 0;
      this.breakpoints = {
        desktop: 990,
        desktop_large: 1440
      };
      this.settings = {
        elements: {
          slider: this.querySelector(this.selectors.slider),
          thumbs: this.querySelector(this.selectors.thumbs)
        },
        instances: {
          slider: null,
          thumbs: null
        },
        options: {
          thumbs: {
            initialSlide: this.selectedMediaIndex,
            spaceBetween: 0,
            slidesPerView: 4,
            watchOverflow: false,
            watchSlidesProgress: false,
            setWrapperSize: false,
            freeMode: true,
            navigation: {
              nextEl: '[data-thumbs-next]',
              prevEl: '[data-thumbs-prev]'
            },
            direction: 'horizontal',
            slideToClickedSlide: true,
            breakpoints: {
              [this.breakpoints.desktop]: {
                slidesPerView: this.hasAttribute('data-thumbs-layout')
                  ? 'auto'
                  : this.getThumbsPerView(),
                direction: this.hasAttribute('data-thumbs-layout')
                  ? 'vertical'
                  : 'horizontal'
              }
            }
          },
          slider: {
            slidesPerView: 1,
            initialSlide: this.selectedMediaIndex,
            centeredSlides: false,
            loop: false,
            spaceBetween: 0,
            // The dots are position indicators only. As 10px clickable
            // buttons they failed the 24px touch-target check, and 24px
            // targets for 18 slides would not fit a phone width. Swiping and
            // the thumbnail rail navigate; with clickable off Swiper renders
            // plain spans (no role="button"/tabindex), identical visually.
            pagination: {
              el: '.swiper-pagination',
              clickable: false
            },
            navigation: {
              prevEl: '[data-arrow-prev]',
              nextEl: '[data-arrow-next]'
            },
            autoHeight: this.hasAttribute('data-slider-autoheight'),
            breakpoints: {
              [this.breakpoints.desktop]: {
                allowTouchMove: false,
                pagination: false,
                navigation: false,
                centeredSlides: false,
                loop: false,
              }
            }
          }

          
        }
      };
    }

    getThumbsPerView(breakpoint) {
      const useAspectRatio = this.hasAttribute(
        'data-slider-autoheight'
      );
      const useLayoutBottom = !this.hasAttribute(
        'data-thumbs-layout'
      );

      if (useLayoutBottom) return breakpoint ? 4.26 : 4.17;

      if (useAspectRatio) return 'auto';

      if (!breakpoint) return 3.49;

      return 3.3;
    }

    connectedCallback() {
      // this.setThumbsHeight();
      this.init();
      this.setLastActiveBreakpoint();
      this.warmRemainingMedia();

      window.addEventListener(
        'resize',
        debounce(() => {
          // this.setThumbsHeight();
          this.initSliders();
          this.setLastActiveBreakpoint();
        }, 300)
      );
    }

    /**
     * Swiper translates the inactive slides out of the viewport, so their
     * `loading="lazy"` images are not fetched until the customer has already
     * swiped or clicked a thumbnail — which is when they sit and watch a blank
     * slide.
     *
     * Once the page itself has finished loading (so nothing here can compete
     * with the LCP image), the slides next to the active one are promoted to
     * eager at low priority, and every slide change warms the next ones. A
     * swipe therefore always paints from cache, without the page downloading
     * the whole gallery up front.
     *
     * This used to warm the first 8 slides on load: ~1.5 MB of 1100w images on
     * mobile that most visitors never swipe to, and Lighthouse's largest
     * "Improve image delivery" item.
     */
    warmRemainingMedia() {
      // A customer who swipes, hovers the gallery or scrolls the desktop grid
      // gets the next two slides right away, whether or not the page has
      // finished loading.
      const interactionEvents = ['touchstart', 'pointerdown', 'pointerover'];
      const warm = () => {
        interactionEvents.forEach((name) =>
          this.removeEventListener(name, warm)
        );
        window.removeEventListener('scroll', warm);

        this.galleryWarm = true;
        this.warmSlides(2);
      };

      interactionEvents.forEach((name) =>
        this.addEventListener(name, warm, { passive: true })
      );
      window.addEventListener('scroll', warm, { passive: true });

      // A visitor who has not touched anything yet only gets the next slide:
      // it covers the first swipe, and the slide after it is warmed as soon
      // as that swipe starts (touchstart above, then slideChange).
      const warmIdle = () => {
        this.galleryWarm = true;
        this.warmSlides(1);
      };

      const schedule = () => {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(warmIdle, { timeout: 2000 });
        } else {
          setTimeout(warmIdle, 500);
        }
      };

      if (document.readyState === 'complete') {
        schedule();
      } else {
        window.addEventListener('load', schedule, { once: true });
      }
    }

    /**
     * Thumbnails past the first few are rendered with data-deferred-src/srcset
     * (see snippets/product-thumbnail.liquid). Restore the ones within half a
     * rail of the visible area, so the next ones are already there
     * when the rail scrolls. Without a thumbs carousel every thumbnail is
     * restored, because nothing would ever scroll them into range.
     */
    hydrateThumbs() {
      const rail = this.settings.elements.thumbs;
      if (!rail) return;

      const pending = rail.querySelectorAll('img[data-deferred-src]');
      if (!pending.length) return;

      const thumbs = this.settings.instances.thumbs;
      const hasCarousel = thumbs && !thumbs.destroyed;
      const box = rail.getBoundingClientRect();
      const ahead = { x: box.width / 2, y: box.height / 2 };

      pending.forEach((img) => {
        if (hasCarousel) {
          const r = (img.closest('.swiper-slide') || img).getBoundingClientRect();
          const inRange = thumbs.isHorizontal()
            ? r.left < box.right + ahead.x && r.right > box.left - ahead.x
            : r.top < box.bottom + ahead.y && r.bottom > box.top - ahead.y;
          if (!inRange) return;
        }

        if (img.dataset.deferredSrcset) img.srcset = img.dataset.deferredSrcset;
        img.src = img.dataset.deferredSrc;
        img.removeAttribute('data-deferred-srcset');
        img.removeAttribute('data-deferred-src');
      });
    }

    /**
     * Non-LCP slides are rendered with data-deferred-src/srcset (see
     * `defer_gallery` in snippets/product-media.liquid) so they cannot compete
     * with the LCP image.
     *
     * - Carousel: only the slide before the active one and the next
     *   `ahead` get their real URLs, eagerly at low priority. Swiper lays
     *   the other slides out in a row to the right, well inside the browser's
     *   lazy-load distance, so restoring their URLs, even as loading="lazy",
     *   would still download every one.
     * - No carousel (desktop grid, data-slider-desktop-disabled): the images
     *   stack in normal flow, so every URL is restored and native lazy loading
     *   fetches each one as it nears the viewport.
     */
    warmSlides(ahead = 2) {
      if (!this.galleryWarm) return;

      const restore = (img) => {
        if (img.dataset.deferredSrcset) img.srcset = img.dataset.deferredSrcset;
        img.src = img.dataset.deferredSrc;
        img.removeAttribute('data-deferred-srcset');
        img.removeAttribute('data-deferred-src');
      };

      const slider = this.settings.instances.slider;
      if (!slider || slider.destroyed) {
        this.querySelectorAll('img[data-deferred-src]').forEach(restore);
        return;
      }

      // Swiper only counts visible slides; filtered-out colours are not
      // reachable until filterMedia() reveals them and calls update().
      const slides = Array.from(slider.slides);
      const from = Math.max(0, slider.activeIndex - 1);
      const to = Math.min(slides.length - 1, slider.activeIndex + ahead);

      for (let i = from; i <= to; i += 1) {
        slides[i].querySelectorAll('img').forEach((img) => {
          if (img.dataset.deferredSrc) restore(img);
          if (img.loading === 'lazy') {
            img.setAttribute('fetchpriority', 'low');
            img.loading = 'eager';
          }
        });
      }
    }

    init() {
      if (typeof PhotoSwipeLightbox !== 'undefined') {
        const photoSwipeLightboxInstance = new PhotoSwipeLightbox({
          gallery: this,
          children: 'a[data-pswp-image]',
          pswpModule: PhotoSwipe
        });

        photoSwipeLightboxInstance.init();
      }

      this.initSliders();
    }

     loadActiveVideo() {
      const activeSlide = this.settings.elements.slider.querySelector('.swiper-slide-active');
            const container = this.querySelector('.product-media') || this;
      if (!activeSlide) return;

       container.querySelectorAll('video, iframe').forEach(el => {
    if (el.tagName === 'VIDEO') el.pause();
    else el.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
  });

      const videoWrapper = activeSlide.querySelector('product-load-video');
      if (!videoWrapper) return;

      if (typeof videoWrapper.loadVideo === 'function') {
        videoWrapper.loadVideo();
      }
    }

    initSliders() {
      const screen =
        window.innerWidth <= this.breakpoints.desktop
          ? 'mobile'
          : 'desktop';
      const disabledOnDesktop = this.hasAttribute(
        'data-slider-desktop-disabled'
      );

      if (screen === 'mobile') {
        if (
          this.lastActiveBreakpoint === 'desktop' &&
          this.swiperInitialized
        ) {
          this.destroySwiper();
        }

        if (!this.swiperInitialized) {
          this.initSwiper();
        }
      } else if (screen === 'desktop') {
        if (disabledOnDesktop) {
          this.swiperInitialized && this.destroySwiper();
          this.warmSlides();
          this.hydrateThumbs();
          return;
        }

        if (
          this.lastActiveBreakpoint === 'mobile' &&
          this.swiperInitialized
        ) {
          this.destroySwiper();
        }

        if (!this.swiperInitialized) {
          this.initSwiper();
        }
      }
    }

    initSwiper() {
      this.swiperInitialized = true;

      // Init thumbs
      if (this.settings.elements.thumbs) {
        this.settings.instances.thumbs = new Swiper(
          this.settings.elements.thumbs,
          this.settings.options.thumbs
        );
        this.settings.options.slider.thumbs = {
          swiper: this.settings.instances.thumbs
        };

        // Settle-point events only: per-frame ones (setTranslate, progress)
        // would measure layout on every frame of a swipe.
        this.settings.instances.thumbs.on(
          'slideChange transitionEnd touchEnd resize',
          () => this.hydrateThumbs()
        );
      }

      // Init main slider
      this.settings.instances.slider = new Swiper(
        this.settings.elements.slider,
        this.settings.options.slider
      );

      // Sync thumbs and main slider
      if (this.settings.elements.thumbs) {
        this.settings.instances.slider.on(
          'slideChangeTransitionStart',
          () => {
            this.settings.instances.thumbs.slideTo(
              this.settings.instances.slider.activeIndex
            );
          }
        );
      }

      if (this.settings.elements.slider) {
        this.settings.instances.thumbs.on(
          'slideChangeTransitionStart',
          () => {
            this.settings.instances.slider.slideTo(
              this.settings.instances.thumbs.activeIndex
            );
          }
        );
      }


      const container = this.querySelector('.product-media') || this;

      // Keep the slides around the new position loaded ahead of the swipe.
      this.settings.instances.slider.on('slideChange', () => this.warmSlides());

      this.settings.instances.slider.on('slideChange', () => {
      container.querySelectorAll('video, iframe').forEach(el => {
        if (el.tagName === 'VIDEO') el.pause();
        else el.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      });

      this.settings.instances.slider.on('init', () => {
        this.loadActiveVideo();
      });

      this.settings.instances.slider.on('slideChangeTransitionEnd', () => {
        this.loadActiveVideo();
      });

      // extra trigger in case "init" event fires late
      this.loadActiveVideo();

      // Re-created after a breakpoint change: warm around the restored slide.
      this.warmSlides();
      this.hydrateThumbs();

    }

    destroySwiper() {
      try {
        this.settings.instances.slider.destroy();
      } catch (error) {}

      try {
        if (this.settings.instances.thumbs) {
          this.settings.instances.thumbs.destroy();
        }
      } catch (error) {}
      this.swiperInitialized = false;
    }

    setLastActiveBreakpoint() {
      this.lastActiveBreakpoint =
        window.innerWidth <= this.breakpoints.desktop
          ? 'mobile'
          : 'desktop';
    }

    setActiveMedia(id) {
      const mediaFound = Array.from(
        this.querySelectorAll(this.selectors.mediaItem)
      ).find(media => Number(media.dataset.mediaId) === id);

      if (!mediaFound) return;
      if (
        !this.settings.instances.slider ||
        this.settings.instances.slider?.destroyed
      ) {
        const headerHeight =
          document.querySelector(this.selectors.header)
            ?.offsetHeight || 0;
        window.scroll({
          top:
            mediaFound.getBoundingClientRect().top +
            window.scrollY -
            headerHeight,
          behavior: 'smooth'
        });
        return;
      }

      this.settings.instances.slider.slideTo(
        Number(mediaFound.dataset.index)
      );
    }

    filterMedia({ name, value }) {
      if (name !== this.filterMediaBy) return;

      const filteredMedia = this.querySelectorAll(
        `${this.selectors.mediaItem}[${this.selectors.mediaColor}="${value}"], ${this.selectors.thumbnail}[${this.selectors.mediaColor}="${value}"]`
      );
      const hasFilteredMedia = filteredMedia.length !== 0;

      this.querySelectorAll(
        `${this.selectors.mediaItem}, ${this.selectors.thumbnail}`
      ).forEach(media => {
        if (!hasFilteredMedia) {
          media.classList.add('swiper-slide');
          media.classList.remove('hidden');
        } else {
          media.classList.remove('swiper-slide');
          media.classList.add('hidden');
        }
      });

      const inactiveSliderInstance =
        !this.settings.instances.slider ||
        this.settings.instances.slider?.destroyed;

      if (!hasFilteredMedia && !inactiveSliderInstance) {
        this.settings.instances.slider.update();
        this.settings.instances.thumbs?.update();
        this.warmSlides();
        return;
      }

      this.querySelectorAll(
        `${this.selectors.mediaItem}[${this.selectors.mediaColor}="${value}"], ${this.selectors.thumbnail}[${this.selectors.mediaColor}="${value}"]`
      ).forEach(media => {
        media.classList.add('swiper-slide');
        media.classList.remove('hidden');
      });

      if (inactiveSliderInstance) return;

      this.settings.instances.slider.update();
      this.settings.instances.thumbs?.update();
      this.warmSlides();
    }

    setThumbsHeight() {
      const thumbs = this.querySelector(
        this.selectors.thumbs
      )?.parentElement;
      if (!thumbs) return;
      const slider = this.querySelector(this.selectors.slider);

      thumbs.style.maxHeight =
        this.hasAttribute('data-thumbs-layout') &&
        window.innerWidth > this.breakpoints.desktop
          ? `${slider.clientHeight}px`
          : '';
    }
  }

  customElements.define('product-media', ProductMedia);
}




// ✅ Final ProductLoadVideo (no template, supports data-src → src)
//
// Load a gallery video only once it is actually on screen (or when
// ProductMedia.loadActiveVideo() makes its slide active). connectedCallback
// used to call loadVideo() unconditionally, so every gallery video - 1080p,
// tens of MB - started downloading during page load and starved the product
// image, the page's LCP, of bandwidth. Inactive swiper slides are clipped by
// the slider's overflow, so they never intersect; the desktop grid layout
// (data-slider-desktop-disabled) loads each video as it scrolls into view.
class ProductLoadVideo extends HTMLElement {
  connectedCallback() {
    if (!('IntersectionObserver' in window)) {
      if (this.closest('.swiper-slide-active')) this.loadVideo();
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.observer.disconnect();
      this.loadVideo();
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  loadVideo() {
    const video = this.querySelector('video');
    if (!video) return;

    const source = video.querySelector('source[data-src]');
    if (source && !source.src) {
      source.src = source.getAttribute('data-src');
      source.removeAttribute('data-src');
      video.load();
    }

    video.play().catch(() => {});
  }
}

customElements.define('product-load-video', ProductLoadVideo);





